import { routeFnFactory } from 'utils/route-factory';

type Id = string | number;

const routes = {
  dashboard: '/',
  leadList: routeFnFactory<{ id: Id }>('/lead-lists/:id'),
  leadLists: '/lead-lists',
  leadListCreate: '/lead-list-import',
  loginCallback: '/login/callback',
  workflowUploadLeadList: routeFnFactory('/campaigns/:campaignId/uploadLeadList'),
  // Unused:
  account: '/account',
  campaignDetails: routeFnFactory('/campaigns/:id'),
  campaignLeadLists: routeFnFactory('/campaigns/:id/lead-lists'),
  campaignList: '/campaigns',
  companyDetails: routeFnFactory('/companies/:id'),
  dncComplainers: '/dnc_litigators',
  dncLitigators: '/dnc_litigators',
  dncs: '/dncs',
  organizationUsers: '/organization/users',
  phoneLookups: '/phone_lookups',
  phoneNumbers: '/phone_numbers',

  login: {
    ...routeFnFactory('/login'),
    redirect: routeFnFactory('/login/redirect'),
    callback: routeFnFactory('/login/callback'),
  },

  home: {
    ...routeFnFactory('/'),
    campaigns: {
      ...routeFnFactory('campaigns'),
      all: routeFnFactory('all'),
      deprovision: routeFnFactory('deprovision'),
    },
    companies: routeFnFactory('companies'),
    agents: {
      ...routeFnFactory('agents'),
      legacy: routeFnFactory('legacy'),
      new: routeFnFactory('new'),
      builder: routeFnFactory('agents/agent-builder'),
    },
    recordings: routeFnFactory('recordings'),
  },

  //
  // COMPANY ROUTES
  //
  company: {
    ...routeFnFactory<{ id: Id }>('/companies/:id'),
    campaigns: routeFnFactory('campaigns'),
    callCenters: routeFnFactory('call-centers'),
    nlpAgents: routeFnFactory('nlp-agents'),
    virtualAgents: routeFnFactory('virtual-agents'),
    outreachTemplates: routeFnFactory('outreach-templates'),
    outreachTemplate: {
      ...routeFnFactory<{ outreachTemplateId: string }>('outreach-templates/:outreachTemplateId'),
      llmConfiguration: routeFnFactory<{
        id: string;
        outreachTemplateId: string;
      }>('/companies/:id/outreach-templates/:outreachTemplateId/llm-configuration'),
    },
    agentConfiguration: {
      ...routeFnFactory<{ id: string; agentConfigId: string }>(
        '/companies/:id/agent-configurations/:agentConfigId',
      ),
      history: routeFnFactory('history'),
    },
    configurations: {
      ...routeFnFactory('configurations'),
      llmConfigurations: routeFnFactory('llm-configurations'),
      guardrails: routeFnFactory('guardrails'),
      guardrail: {
        ...routeFnFactory<{ guardrailId: string; global?: 'global'; id: string }>(
          'guardrails/:global?/:guardrailId',
        ),
      },
    },
    brands: routeFnFactory('brands'),
    scripts: routeFnFactory('scripts'),
    users: routeFnFactory('users'),
    ivrs: routeFnFactory('ivrs'),
    userPool: routeFnFactory('user-pool'),
    settings: {
      ...routeFnFactory('settings'),
      organization: routeFnFactory('organization'),
      webhooks: routeFnFactory('webhooks'),
      integrations: {
        ...routeFnFactory('integrations'),
        goHighLevel: routeFnFactory('high-level'),
        hubspot: routeFnFactory('hubspot'),
        leadIngestion: routeFnFactory('lead-ingestion'),
        appointmentScheduling: routeFnFactory('appointment-scheduling'),
      },
    },
    callCenter: routeFnFactory<{ id: string; callCenterId: string }>(
      '/companies/:id/call-centers/:callCenterId',
    ),
    reports: {
      ...routeFnFactory('reports'),
      endContact: routeFnFactory('end-contact'),
      notPosted: routeFnFactory('not-posted'),
      undeliverable: routeFnFactory('undeliverable'),
      dnc: routeFnFactory('dnc'),
      transfers: routeFnFactory('transfers'),
      posted: routeFnFactory('posted'),
    },
    conversations: routeFnFactory('conversations'),
    conversationDashboard: routeFnFactory('conversation-dashboard'),
    analytics: routeFnFactory('analytics'),
    leads: {
      ...routeFnFactory('leads'),
      lists: routeFnFactory('lists'),
      posting: routeFnFactory('posting'),
      imports: routeFnFactory('imports'),
      pending: routeFnFactory('pending'),
      importDetail: routeFnFactory<{ leadListId: string }>('imports/:leadListId'),
      importCreate: routeFnFactory('imports/new'),
      auditTrail: routeFnFactory('audit-trail'),
    },
    recordings: routeFnFactory<{ id: Id }>('/companies/:id/recordings'),

    //
    // CAMPAIGN ROUTES
    //
    campaign: {
      ...routeFnFactory<{ id: Id; campaignId: Id }>('/companies/:id/campaigns/:campaignId'),
      conversation: routeFnFactory('conversation'),
      settings: routeFnFactory('settings'),
      chatConversation: routeFnFactory('chat-conversation'),
      conversationWithOutreachTemplateId: routeFnFactory<{
        companyId: string;
        campaignId: string;
        outreachTemplateId: string;
      }>('/companies/:companyId/campaigns/:campaignId/conversation?oti=:outreachTemplateId'),
      callCenter: routeFnFactory('call-center'),
      leadSource: routeFnFactory('lead-source'),
      leadLists: routeFnFactory('lead-lists'),
      leadList: routeFnFactory<{ leadListId: string }>('lead-lists/:leadListId'),
      leadDelivery: routeFnFactory('lead-delivery'),
      agentPhones: routeFnFactory('agent-phones'),
      a2P: routeFnFactory('a2p'),
      dnc: routeFnFactory('dnc'),
      testLeads: routeFnFactory('test-leads'),
      outreachTemplate: routeFnFactory('outreach-template'),
      experiments: routeFnFactory('experiments'),
      analytics: routeFnFactory('analytics'),
      llmConfiguration: routeFnFactory('llm-configuration'),
      vendors: routeFnFactory('vendors'),
      vendorsAbs: routeFnFactory<{ id: Id; campaignId: Id }>(
        '/companies/:id/campaigns/:campaignId/vendors',
      ),
    },
  },

  campaign: {
    ...routeFnFactory<{ campaignId: string }>('/campaigns/:campaignId'),
    conversation: routeFnFactory('conversation'),
    settings: routeFnFactory('settings'),
    ivrs: routeFnFactory('ivrs'),
    conversationWithPhone: routeFnFactory<{
      campaignId: string;
      phone: string;
    }>('/campaigns/:campaignId/conversation?phone=:phone'),
    outreachTemplates: routeFnFactory('outreach-templates'),
    outreachTemplate: routeFnFactory<{ outreachTemplateId: string }>(
      'outreach-templates/:outreachTemplateId',
    ),
    experiments: routeFnFactory('experiments'),
    analytics: routeFnFactory('analytics'),
  },

  conversation: routeFnFactory<{
    id: string;
    campaignId: string;
    phone: string;
  }>('/companies/:id/campaigns/:campaignId/conversation?phone=:phone'),

  absolute: {
    outreachTemplates: routeFnFactory<{ companyId: Id }>(
      '/companies/:companyId/outreach-templates',
    ),
    outreachTemplate: routeFnFactory<{ companyId: Id; outreachTemplateId: string }>(
      '/companies/:companyId/outreach-templates/:outreachTemplateId',
    ),
  },

  conversationFeed: routeFnFactory('conversation-feed'),
  leads: {
    ...routeFnFactory('/leads'),
    lists: routeFnFactory('lists'),
    imports: routeFnFactory('imports'),
    pending: routeFnFactory('pending'),
    importDetail: routeFnFactory<{ leadListId: string }>('imports/:leadListId'),
    importCreate: routeFnFactory('imports/new'),
    auditTrail: routeFnFactory('audit-trail'),
  },

  reports: {
    ...routeFnFactory('/reports'),
    endContact: routeFnFactory('end-contact'),
    notPosted: routeFnFactory('not-posted'),
    undeliverable: routeFnFactory('undeliverable'),
    dnc: routeFnFactory('dnc'),
    transfers: routeFnFactory('transfers'),
    posted: routeFnFactory('posted'),
  },

  analytics: routeFnFactory('/analytics'),
  ivrs: routeFnFactory('/ivrs'),

  settings: {
    ...routeFnFactory('/settings'),
    organization: routeFnFactory('/settings/organization'),
    webhooks: routeFnFactory('/settings/webhooks'),
    integrations: {
      ...routeFnFactory('/settings/integrations'),
      oldHighLevel: routeFnFactory('go-high-level'),
      goHighLevel: routeFnFactory('high-level'),
      hubspot: routeFnFactory('hubspot'),
      leadIngestion: routeFnFactory('lead-ingestion'),
      appointmentScheduling: routeFnFactory('appointment-scheduling'),
      goHighLevelAbsolute: routeFnFactory('/settings/integrations/high-level'),
      hubspotAbsolute: routeFnFactory('/settings/integrations/hubspot'),
    },
  },

  llmConfigurations: routeFnFactory<{ companyId: string }>(
    '/companies/:companyId/llm-configurations',
  ),

  llmConfiguration: {
    ...routeFnFactory<{ companyId: string; llmConfigurationId: string }>(
      '/companies/:companyId/llm-configurations/:llmConfigurationId',
    ),
  },

  guardrailConfiguration: {
    ...routeFnFactory<{ companyId: string; guardrailId: string }>(
      '/companies/:companyId/configurations/guardrails/:guardrailId',
    ),
  },

  guardrailConfigurationGlobal: {
    ...routeFnFactory<{ companyId: string; guardrailId: string }>(
      '/companies/:companyId/configurations/guardrails/global/:guardrailId',
    ),
  },
};

export default routes;
